import API_BASE_URL from './config';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './JudgeIconMeritEntry.css';

function JudgeIconMeritEntry() {
  const { showId } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [entries, setEntries] = useState([]);
  const [currentEntryIndex, setCurrentEntryIndex] = useState(0);
  const [formData, setFormData] = useState({ score: {}, comments: '' });
  const [hasHandler, setHasHandler] = useState(false);
  const [placings, setPlacings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});

  const backendUrl = `${API_BASE_URL}`;

  // Default rubric for old shows (exact match to your original system)
  const defaultRubric = {
    components: [
      { name: 'Show Depiction', points: 10, originalKey: 'showDepiction' },
      { name: 'Dog Accuracy', points: 6, originalKey: 'dogAccuracy' },
      { name: 'Artistic Quality', points: 2, originalKey: 'artisticQuality' },
      { name: 'Creativity', points: 2, originalKey: 'creativity' },
    ],
    bonuses: [
      { name: 'Short Story', points: 0, originalKey: 'shortStory' },
      { name: 'Extra Images', points: 0, originalKey: 'extraImages' },
      { name: 'Handler', points: 0, originalKey: 'handler' },
    ],
  };

  // Legacy mapping so old saved entries still load correctly
  const legacyScoreKey = {
    shortStory: 'storyBonus',
    extraImages: 'imageBonus',
    handler: 'handlerBonus',
  };

  const activeRubric = show?.rubric || defaultRubric;

  // Helper to build score object (keys = originalKey from rubric)
  const initializeScoreForEntry = (entry) => {
    const scoreObj = {};

    // Components
    activeRubric.components.forEach((comp) => {
      scoreObj[comp.originalKey] =
        entry.judged && entry.score ? entry.score[comp.originalKey] ?? 0 : 0;
    });

    // Bonuses
    activeRubric.bonuses.forEach((bonus) => {
      const key = bonus.originalKey;
      let val = entry.judged && entry.score ? (entry.score[legacyScoreKey[key] || key] ?? 0) : 0;

      if (key === 'shortStory') {
        const wordCount = entry.shortStory ? entry.shortStory.trim().split(/\s+/).length : 0;
        const maxPoints = bonus.points || 5;
        val = Math.min(maxPoints, Math.floor(wordCount / 100));
      }

      scoreObj[key] = val;
    });

    return scoreObj;
  };

  useEffect(() => {
    const playerId = localStorage.getItem('playerId');
    if (!playerId) {
      setSubmitError('Please log in as an admin');
      navigate('/login');
      return;
    }

    async function fetchData() {
      try {
        setLoading(true);
        console.log('Fetching show:', showId);

        const showResponse = await fetch(`${backendUrl}/merit-confo/shows/${showId}`, {
          headers: { 'player-id': playerId },
        });
        const showData = await showResponse.json();
        if (!showResponse.ok) throw new Error(showData.error);
        setShow(showData);
        console.log('Show details:', showData);

        console.log('Fetching entries for show:', showId);
        const entriesResponse = await fetch(`${backendUrl}/iconmerit/judging/entries/${showId}`, {
          headers: { 'player-id': playerId },
        });
        const entriesData = await entriesResponse.json();
        if (!entriesResponse.ok) throw new Error(entriesData.error);

        if (entriesData.cancelled) {
          setSubmitError(entriesData.message);
          setTimeout(() => navigate('/judge-closed-icon-merit'), 5000);
          setLoading(false);
          return;
        }

        console.log('Fetched entries:', entriesData);
        setEntries(entriesData);

        // Placings (still empty until finalize - that's normal)
        const placingsData = showData.placings || [];
        console.log('Raw placings:', placingsData);
        const enrichedPlacings = placingsData.map((placing) => {
          const entry = entriesData.find((e) => e._id === placing.entryId);
          if (!entry || !entry.dogId) return { ...placing, dogName: 'Unknown', breed: 'Unknown', class: 'Unknown' };
          return {
            ...placing,
            dogName: entry.dogId.registeredName || 'Unknown',
            breed: entry.dogId.breed || 'Unknown',
            gender: entry.dogId.gender || 'Unknown',
            class: entry.class || 'Unknown',
            breeder: entry.playerId?.username || 'Unknown',
          };
        });
        console.log('Enriched placings:', enrichedPlacings);
        setPlacings(enrichedPlacings);

        if (entriesData.length > 0) {
          const currentEntry = entriesData[0];
          setFormData({
            score: initializeScoreForEntry(currentEntry),
            comments: currentEntry.judged ? currentEntry.comments : '',
          });
          setHasHandler(!!currentEntry.staffId && currentEntry.judged && (currentEntry.score?.handlerBonus || currentEntry.score?.handler || 0) > 0);
        }
        setLoading(false);
      } catch (err) {
        setSubmitError(`Failed to load data: ${err.message}`);
        setLoading(false);
      }
    }
    fetchData();
  }, [showId, navigate]);

  useEffect(() => {
    if (entries.length > 0) {
      const currentEntry = entries[currentEntryIndex];
      setFormData({
        score: initializeScoreForEntry(currentEntry),
        comments: currentEntry.judged ? currentEntry.comments : '',
      });
      setHasHandler(!!currentEntry.staffId && currentEntry.judged && (currentEntry.score?.handlerBonus || currentEntry.score?.handler || 0) > 0);
    }
  }, [currentEntryIndex, entries]);

  // Auto-clear messages
  useEffect(() => { if (successMessage) { const t = setTimeout(() => setSuccessMessage(''), 5000); return () => clearTimeout(t); } }, [successMessage]);
  useEffect(() => { if (submitError) { const t = setTimeout(() => setSubmitError(''), 5000); return () => clearTimeout(t); } }, [submitError]);

  const handleScoreChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      score: { ...prev.score, [key]: parseInt(value) || 0 },
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'hasHandler') {
      setHasHandler(checked);
      if (!checked) {
        // zero handler bonus
        const handlerKey = activeRubric.bonuses.find((b) => b.originalKey === 'handler')?.originalKey;
        if (handlerKey) {
          setFormData((prev) => ({
            ...prev,
            score: { ...prev.score, [handlerKey]: 0 },
          }));
        }
      }
    } else if (name === 'comments') {
      setFormData((prev) => ({ ...prev, comments: value }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const { score } = formData;

    activeRubric.components.forEach((comp) => {
      const val = score[comp.originalKey] ?? 0;
      if (val < 0 || val > comp.points) newErrors[comp.originalKey] = `0–${comp.points} points`;
    });

    activeRubric.bonuses.forEach((bonus) => {
      const key = bonus.originalKey;
      let max = bonus.points || 0;
      if (key === 'shortStory') max = max || 5;
      if (key === 'extraImages') max = max || 9;
      if (key === 'handler') max = max || 5;

      const val = score[key] ?? 0;
      if (val < 0 || val > max) newErrors[key] = `0–${max} points`;
      if (key === 'handler' && !hasHandler && val !== 0) newErrors[key] = 'Handler bonus must be 0 if no handler';
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSuccessMessage('');
    if (!validateForm()) return;

    try {
      const playerId = localStorage.getItem('playerId');

      // Build score object using legacy keys for old bonuses (keeps backend happy)
      const scoreToSend = {};
      activeRubric.components.forEach((c) => {
        scoreToSend[c.originalKey] = formData.score[c.originalKey] ?? 0;
      });
      activeRubric.bonuses.forEach((b) => {
        const sendKey = legacyScoreKey[b.originalKey] || b.originalKey;
        scoreToSend[sendKey] = formData.score[b.originalKey] ?? 0;
      });

      const response = await fetch(`${backendUrl}/iconmerit/judging/score/${entries[currentEntryIndex]._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'player-id': playerId },
        body: JSON.stringify({ score: scoreToSend, comments: formData.comments, hasHandler }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setSuccessMessage('Entry scored successfully!');
      const updatedEntries = [...entries];
      updatedEntries[currentEntryIndex] = data;
      setEntries(updatedEntries);

      if (currentEntryIndex < entries.length - 1) {
        setCurrentEntryIndex(currentEntryIndex + 1);
        setFormData({ score: {}, comments: '' });
        setHasHandler(false);
      }
    } catch (err) {
      setSubmitError(`Failed to submit score: ${err.message}`);
    }
  };

  const handleFinalize = async () => {
    if (!window.confirm('Finalize judging for this show? This cannot be undone.')) return;

    // FIXED: Check actual entry scores instead of empty placings
    const hasQualifyingCACIB = entries.some((e) => {
      if (!['Open', 'Bred-By', 'Champion'].includes(e.class)) return false;
      return (e.score?.total ?? 0) >= 15;
    });

    if (!hasQualifyingCACIB && entries.some((e) => e.class !== 'Veteran')) {
      setSubmitError('No CACIB awards generated. Ensure at least one entry has a score ≥ 15 in Open, Bred-By, or Champion class.');
      return;
    }

    try {
      const playerId = localStorage.getItem('playerId');
      const response = await fetch(`${backendUrl}/iconmerit/judging/finalize/${showId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'player-id': playerId },
        body: JSON.stringify({ cacibAwards: [] }), // backend will compute them
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      setSuccessMessage('Show judging finalized! Redirecting in 5 seconds...');
      setTimeout(() => navigate('/judge-closed-icon-merit'), 5000);
    } catch (err) {
      setSubmitError(`Failed to finalize judging: ${err.message}`);
    }
  };

  const handlePrevious = () => { if (currentEntryIndex > 0) setCurrentEntryIndex(currentEntryIndex - 1); };
  const handleNext = () => { if (currentEntryIndex < entries.length - 1) setCurrentEntryIndex(currentEntryIndex + 1); };

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (submitError) return <div className="error-message">{submitError}</div>;
  if (!show || entries.length === 0) return <div className="error-message">No entries found</div>;

  const entry = entries[currentEntryIndex];

  return (
    <div className="judge-icon-merit-entry">
      <h2>Judge iCON Merit Show: {show.name}</h2>
      <div className="show-details">
        <p><strong>Location:</strong> {show.location}</p>
        <p><strong>Region:</strong> {show.region}</p>
        {show.specialtyBreed && <p><strong>Specialty Breed:</strong> {show.specialtyBreed}</p>}
        {show.featuredJudge && <p><strong>Featured Judge:</strong> {show.featuredJudge}</p>}
        <p><strong>Entry {currentEntryIndex + 1} of {entries.length}</strong></p>
      </div>

      <div className="entry-item">
        <p><strong>Dog:</strong> {entry.dogId.registeredName} ({entry.dogId.breed}, {entry.dogId.gender})</p>
        <p><strong>Player:</strong> {entry.playerId.username}</p>
        <p><strong>Class:</strong> {entry.class}</p>
        <p><strong>Primary Art:</strong> <a href={entry.primaryArtUrl} target="_blank" rel="noopener noreferrer" className="art-link">View Art</a></p>
        {entry.shortStory && (
          <p>
            <strong>Story ({entry.shortStory.trim().split(/\s+/).length} words):</strong> {entry.shortStory}
          </p>
        )}
        {entry.extraImages.length > 0 && (
          <p>
            <strong>Extra Images:</strong>{' '}
            {entry.extraImages.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="art-link">Image {i + 1} </a>
            ))}
          </p>
        )}
        <p><strong>Handler:</strong> {entry.staffId ? entry.staffId.staffName : 'None'}</p>
        {entry.staffId && activeRubric.bonuses.some((b) => b.originalKey === 'handler') && (
          <div className="form-group">
            <label>
              <input type="checkbox" name="hasHandler" checked={hasHandler} onChange={handleInputChange} />
              Confirm Handler Presence
            </label>
          </div>
        )}
        <p><strong>Item:</strong> {entry.usedItem ? `${entry.usedItem.name} (+${entry.usedItem.showBonus} points)` : 'None'}</p>
      </div>

      <form onSubmit={handleSubmit} className="entry-form">
        <h3>Components</h3>
        {activeRubric.components.map((comp) => (
          <div key={comp.originalKey} className="form-group">
            <label>{comp.name} (0–{comp.points}):</label>
            <input
              type="number"
              value={formData.score[comp.originalKey] ?? 0}
              onChange={(e) => handleScoreChange(comp.originalKey, e.target.value)}
              min="0"
              max={comp.points}
              className={errors[comp.originalKey] ? 'error' : ''}
            />
            {errors[comp.originalKey] && <span className="error-text">{errors[comp.originalKey]}</span>}
          </div>
        ))}

        <h3>Bonuses</h3>
        {activeRubric.bonuses.map((bonus) => {
          const key = bonus.originalKey;
          let maxPoints = bonus.points || 0;
          if (key === 'shortStory') maxPoints = maxPoints || 5;
          if (key === 'extraImages') maxPoints = maxPoints || 9;
          if (key === 'handler') maxPoints = maxPoints || 5;

          if (key === 'shortStory') {
            const wordCount = entry.shortStory ? entry.shortStory.trim().split(/\s+/).length : 0;
            const autoValue = Math.min(maxPoints, Math.floor(wordCount / 100));
            return (
              <div key={key} className="form-group">
                <label>{bonus.name} (auto, up to {maxPoints}):</label>
                <p><strong>{autoValue}</strong> points ({wordCount} words)</p>
              </div>
            );
          }

          if (key === 'handler' && !hasHandler) return null;

          return (
            <div key={key} className="form-group">
              <label>{bonus.name} (0–{maxPoints}):</label>
              <input
                type="number"
                value={formData.score[key] ?? 0}
                onChange={(e) => handleScoreChange(key, e.target.value)}
                min="0"
                max={maxPoints}
                className={errors[key] ? 'error' : ''}
              />
              {errors[key] && <span className="error-text">{errors[key]}</span>}
            </div>
          );
        })}

        <div className="form-group">
          <label>Judge’s Comments:</label>
          <textarea name="comments" value={formData.comments} onChange={handleInputChange} rows="4" placeholder="Enter comments..." />
        </div>

        <div className="button-group">
          <button type="button" onClick={handlePrevious} disabled={currentEntryIndex === 0} className="nav-button">Previous Entry</button>
          <button type="submit" className="submit-button">Submit Score</button>
          <button type="button" onClick={handleNext} disabled={currentEntryIndex === entries.length - 1} className="nav-button">Next Entry</button>
        </div>
      </form>

      {successMessage && <div className="success-message">{successMessage}</div>}
      {submitError && <div className="error-message">{submitError}</div>}

      {!entries.some((e) => !e.judged) && (
        <div className="finalize-section">
          <h3>Show Placings</h3>
          {placings.length === 0 ? (
            <p>No placings available yet. Click Finalize to generate them.</p>
          ) : (
            /* your existing placings render code */
            <div className="placings-container">
              {Object.entries({
                Breed: placings.filter((p) => ['Winners Dog', 'Winners Bitch', 'Best of Breed', 'Best of Opposite Sex', 'Best of Winners'].includes(p.award)),
                Group: placings.filter((p) => ['Group First', 'Group Second', 'Group Third', 'Group Fourth'].includes(p.award)),
                BestInShow: placings.filter((p) => ['Best in Show', 'Reserve Best in Show'].includes(p.award)),
                CACIB: placings.filter((p) => p.award.includes('CACIB')),
              }).map(([category, awards]) =>
                awards.length > 0 && (
                  <div key={category} className="placings-group">
                    <h4>{category}</h4>
                    <div className="placings-cards">
                      {awards.map((placing, i) => (
                        <div key={i} className="placing-card">
                          <p><strong>{placing.award}</strong>: {placing.dogName} ({placing.breed})</p>
                          <p><strong>Owner:</strong> {placing.breeder}</p>
                          <p><strong>Class:</strong> {placing.class}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
          <button onClick={handleFinalize} className="finalize-button">Finalize Judging</button>
        </div>
      )}
    </div>
  );
}

export default JudgeIconMeritEntry;